import {
  ComponentPropsWithoutRef,
  useEffect,
  useRef,
  useState,
} from "react";

import { VariantProps, cva } from "class-variance-authority";
import { SlArrowDown, SlArrowUp } from "react-icons/sl";

import { organization } from "@/constants/common";
import useModal from "@/hooks/useModal";
import { useOutSideClick } from "@/hooks/useOutSideClick";
import { cn } from "@/utils/cn";

const dropdownCSS = cva("relative h-10", {
  variants: {
    variant: {
      default: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface DropdownProps
  extends ComponentPropsWithoutRef<"input">,
    VariantProps<typeof dropdownCSS> {
  onChangeOption: (option: string) => void;
  disabled: boolean;
  category: string;
  options: any[];
  selectedOption: string;
}

const Dropdown = ({
  variant,
  onChangeOption,
  className,
  disabled,
  category,
  options,
  selectedOption,
}: DropdownProps) => {
  const [optionList, setOptionList] = useState<{ id: number; name: string }[]>(
    [],
  );

  useEffect(() => {
    if (typeof options[0] === "string") {
      setOptionList(options.map(option => ({ id: option, name: option })));
    } else {
      setOptionList(options);
    }
  }, [options]);

  const placeholder = organization[category];
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsModal = useModal();
  useOutSideClick(dropdownRef, () => optionsModal.onClose());

  const onClickOption = (option: any) => {
    onChangeOption(option);
    optionsModal.onClose();
  };

  return (
    <div ref={dropdownRef} className={cn(dropdownCSS({ variant }), className)}>
      {/* Trigger */}
      <button
        type="button"
        className={cn(
          "absolute top-0 z-40 flex h-10 w-full select-none items-center justify-between rounded-lg border bg-white px-3 text-sm transition-colors",
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
            : "border-gray-300 hover:border-primary/50",
          optionsModal.isOpen && !disabled && "border-primary ring-1 ring-primary/20",
          selectedOption ? "text-black" : "text-gray-400",
        )}
        onClick={() => {
          if (!disabled) optionsModal.onToggle();
        }}
        disabled={disabled}
      >
        <span className="truncate">
          {selectedOption || placeholder}
        </span>
        {!disabled && (
          optionsModal.isOpen
            ? <SlArrowUp size={12} className="shrink-0 text-gray-400" />
            : <SlArrowDown size={12} className="shrink-0 text-gray-400" />
        )}
      </button>

      {/* Options */}
      {optionsModal.isOpen && (
        <div className="absolute left-0 top-11 z-50 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="max-h-60 overflow-y-auto py-1">
            {optionList.map(option => (
              <button
                key={option.id}
                type="button"
                className={cn(
                  "flex w-full select-none items-center px-3 py-2 text-sm transition-colors",
                  selectedOption === option.name
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-gray-700 hover:bg-gray-50",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickOption(option);
                }}
              >
                {option.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
